import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import HistoryIcon from '@mui/icons-material/History';
import qs from 'qs';
import {
  approveMemberAppLink,
  getMemberAppLinkPreviousTarget,
  getMemberAppLinkSamePhoneCandidates,
} from '../../api/memberAppLink';
import { getMemberByBranchAndTemporary } from '../../api/member-management';
import CommonModalLayout from '../../form-components/CommonModalLayout';
import CommonModalButton from '../../form-components/CommonModalButton';
import LinkCompare from './LinkCompare';
import {
  ChoiceCard,
  ChoiceDesc,
  ChoiceRow,
  ChoiceTitle,
  CompareCard,
  CompareHeading,
  CompareLine,
  EmptyText,
  ModeBadge,
  NewTemporaryRow,
  PreviousLinkDates,
  PreviousLinkInfo,
  WarningText,
  ResultItem,
  ResultList,
  ResultSub,
  SearchInput,
  SearchRow,
} from './MemberAppLinkStyled';

const TEMPORARY_USER_TYPE = 'T';

// 승인 방법 — 예전 연동 데이터와 연결 / CRM 에 있는 회원을 찾아 연결 / 잠재회원으로 새로 등록
const MODE_PREVIOUS = 'previous';
const MODE_SEARCH = 'search';
const MODE_NEW = 'new';

// 고른 대상 대신 "잠재회원으로 새로 등록" 을 고른 경우의 표시값
const NEW_TEMPORARY = { isNew: true, userType: TEMPORARY_USER_TYPE };

// 검색 결과 행 → 승인 API 의 대상 지정 바디. 새로 등록이면 서버가 앱 계정 정보로 잠재회원을 만든다
const toApproveBody = target =>
  target.isNew
    ? { targetType: 'TEMPORARY_USER', createTemporaryUser: true }
    : target.userType === TEMPORARY_USER_TYPE
    ? { targetType: 'TEMPORARY_USER', targetTemporaryUserId: target.memberId }
    : { targetType: 'MEMBER', targetMemberId: target.memberId };

// 예전 연동 대상(previous-target 응답) → 검색 결과 행과 같은 모양. 잠재회원은 temporaryUserId 를 memberId 자리에 둔다
const toPreviousSelection = target =>
  target.targetType === 'TEMPORARY_USER'
    ? {
        memberId: target.temporaryUserId,
        userType: TEMPORARY_USER_TYPE,
        name: target.name,
        phoneNumber: target.phoneNumber,
        isPrevious: true,
      }
    : { memberId: target.memberId, name: target.name, phoneNumber: target.phoneNumber, isPrevious: true };

// "yyyy-MM-dd HH:mm:ss" → "yyyy-MM-dd"
const toDate = dateTime => dateTime?.slice(0, 10);

/**
 * 대기 요청 승인 — 먼저 방법(CRM 회원 찾아 연결 / 잠재회원 새로 등록)을 고르고, 확인 단계에서 승인한다.
 */
const ApproveLinkModal = ({ open, onClose, link }) => {
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [searched, setSearched] = useState('');
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState(MODE_SEARCH);
  // 열릴 때마다 기본 방법을 한 번만 정한다. 조회 전에 직원이 카드를 고르면 그 선택을 덮지 않는다
  const defaultModeApplied = useRef(false);

  useEffect(() => {
    if (open) {
      setKeyword('');
      setSearched('');
      setSelected(null);
      setMode(MODE_SEARCH);
      defaultModeApplied.current = false;
    }
  }, [open]);

  const { data: previous, isSuccess: isPreviousLoaded } = useQuery({
    queryKey: ['memberAppLinkPreviousTarget', link?.id],
    queryFn: () => getMemberAppLinkPreviousTarget(link.id),
    enabled: open && !!link?.id,
  });
  const previousTarget = previous?.target ?? null;

  useEffect(() => {
    if (!open || !isPreviousLoaded || defaultModeApplied.current) return;
    defaultModeApplied.current = true;
    if (previousTarget) setMode(MODE_PREVIOUS);
  }, [open, isPreviousLoaded, previousTarget]);

  const { data: samePhoneData } = useQuery({
    queryKey: ['memberAppLinkSamePhoneCandidates', link?.id],
    queryFn: () => getMemberAppLinkSamePhoneCandidates(link.id),
    enabled: open && !!link?.id,
  });
  const samePhoneCandidates = Array.isArray(samePhoneData) ? samePhoneData : [];

  const chooseMode = nextMode => {
    defaultModeApplied.current = true;
    setMode(nextMode);
  };

  const { data, isFetching } = useQuery({
    queryKey: ['memberAppLinkTargetSearch', user.branch?.branchId, searched],
    queryFn: () =>
      getMemberByBranchAndTemporary(
        user.token,
        qs.stringify({
          searchInput: searched,
          headBranchId: user.headBranch?.headBranchId,
          branchId: user.branch?.branchId,
        }),
      ),
    enabled: open && !!searched,
  });
  const results = data?.content ?? [];

  const approveMutation = useMutation({
    mutationFn: () => approveMemberAppLink(link.id, toApproveBody(selected)),
    onSuccess: result => {
      toast.success('연동을 승인했습니다.');
      if (result?.warning) toast.warning(result.warning);
      queryClient.invalidateQueries({ queryKey: ['memberAppLinks'] });
      queryClient.invalidateQueries({ queryKey: ['memberAppLinkPendingCount'] });
      // 병합으로 이름·번호가 바뀌므로 대상 상세와 연동 이력도 새로 받는다.
      queryClient.invalidateQueries({ queryKey: ['memberAppLinkHistory'] });
      queryClient.invalidateQueries({ queryKey: ['user-detail'] });
      queryClient.invalidateQueries({ queryKey: ['temporaryUser'] });
      // 새로 등록하고 승인했으면 잠재회원 목록·건수에 바로 보이게
      queryClient.invalidateQueries({ queryKey: ['temporaryUsers'] });
      queryClient.invalidateQueries({ queryKey: ['temporaryUserSummary'] });
      onClose();
    },
  });

  // 더블클릭이 리렌더 전에 두 번 들어오는 것을 막는다(isPending 은 리렌더 후에야 반영된다).
  const isApproving = useRef(false);
  const runApproveMutation = () => {
    if (isApproving.current) return;
    isApproving.current = true;
    approveMutation.mutate(undefined, { onSettled: () => (isApproving.current = false) });
  };

  const handleSearch = () => setSearched(keyword.trim());

  if (!link) return null;

  const isConfirmStep = !!selected;
  const targetTypeLabel = selected?.userType === TEMPORARY_USER_TYPE ? '잠재회원' : '회원';
  const previousTypeLabel = previousTarget?.targetType === 'TEMPORARY_USER' ? '잠재회원' : '회원';
  const previousDates = [
    previous?.lastLinkedAt && `연동 ${toDate(previous.lastLinkedAt)}`,
    previous?.lastUnlinkedAt && `해제 ${toDate(previous.lastUnlinkedAt)}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <CommonModalLayout
      open={open}
      onClose={onClose}
      title={isConfirmStep ? '연동 확인' : '연동 승인'}
      description={isConfirmStep ? undefined : '승인 방법을 먼저 골라 주세요.'}
      width={640}
      footer={
        isConfirmStep ? (
          <>
            <CommonModalButton
              variant="cancel"
              onClick={() => setSelected(null)}
              disabled={approveMutation.isPending}>
              이전
            </CommonModalButton>
            <CommonModalButton onClick={runApproveMutation} disabled={approveMutation.isPending}>
              {selected.isNew ? '잠재회원 등록하고 승인' : '연결하고 승인'}
            </CommonModalButton>
          </>
        ) : mode === MODE_NEW ? (
          <>
            <CommonModalButton variant="cancel" onClick={onClose}>
              닫기
            </CommonModalButton>
            <CommonModalButton onClick={() => setSelected(NEW_TEMPORARY)}>다음</CommonModalButton>
          </>
        ) : mode === MODE_PREVIOUS && previousTarget ? (
          <>
            <CommonModalButton variant="cancel" onClick={onClose}>
              닫기
            </CommonModalButton>
            <CommonModalButton onClick={() => setSelected(toPreviousSelection(previousTarget))}>다음</CommonModalButton>
          </>
        ) : (
          <CommonModalButton variant="cancel" onClick={onClose}>
            닫기
          </CommonModalButton>
        )
      }>
      {isConfirmStep && selected.isNew ? (
        <>
          <ModeBadge>
            <PersonAddAlt1Icon sx={{ fontSize: 18 }} />
            잠재회원으로 새로 등록
          </ModeBadge>
          <CompareCard>
            <CompareHeading>앱 계정</CompareHeading>
            <CompareLine>이름 {link.appAccount?.name || '-'}</CompareLine>
            <CompareLine>아이디 {link.appAccount?.username || '-'}</CompareLine>
            <CompareLine>번호 {link.appAccount?.phoneNumber || '-'}</CompareLine>
          </CompareCard>
          <NewTemporaryRow sx={{ marginTop: '16px' }}>
            이 앱 계정 정보로 이 지점에 잠재회원을 새로 등록하고 연동합니다.
          </NewTemporaryRow>
          <WarningText>
            {previousTarget
              ? '예전 연동 데이터가 있어요. 새로 등록하면 그 데이터와 별개인 잠재회원이 하나 더 생깁니다.'
              : '센터에 같은 사람이 이미 있으면 중복 회원이 생깁니다. 있다면 [이전]에서 [CRM 회원 찾아서 연결]을 골라 주세요.'}
          </WarningText>
        </>
      ) : isConfirmStep ? (
        <>
          {selected.isPrevious ? (
            <ModeBadge>
              <HistoryIcon sx={{ fontSize: 18 }} />
              예전 연동 데이터와 연결
            </ModeBadge>
          ) : (
            <ModeBadge>
              <PersonSearchIcon sx={{ fontSize: 18 }} />
              CRM {targetTypeLabel}과 연결
            </ModeBadge>
          )}
          <LinkCompare
            appAccount={link.appAccount}
            target={{ name: selected.name, phoneNumber: selected.phoneNumber, typeLabel: targetTypeLabel }}
          />
        </>
      ) : (
        <>
          {previous?.linkedBefore ? (
            <PreviousLinkInfo>
              이 앱 계정은 예전에 이 지점과 연동한 기록이 있어요.
              {previousDates && <PreviousLinkDates>{previousDates}</PreviousLinkDates>}
            </PreviousLinkInfo>
          ) : (
            previousTarget && (
              <PreviousLinkInfo>이 앱 계정은 이미 이 지점에 기록(상품·포인트 등)이 있는 회원이에요.</PreviousLinkInfo>
            )
          )}
          <ChoiceRow>
            {previousTarget && (
              <ChoiceCard type="button" active={mode === MODE_PREVIOUS} onClick={() => chooseMode(MODE_PREVIOUS)}>
                <ChoiceTitle active={mode === MODE_PREVIOUS}>
                  <HistoryIcon sx={{ fontSize: 20 }} />
                  예전 연동 데이터와 연결
                </ChoiceTitle>
                <ChoiceDesc>예전에 이 지점에서 쓰던 기록에 그대로 연결합니다.</ChoiceDesc>
              </ChoiceCard>
            )}
            <ChoiceCard type="button" active={mode === MODE_SEARCH} onClick={() => chooseMode(MODE_SEARCH)}>
              <ChoiceTitle active={mode === MODE_SEARCH}>
                <PersonSearchIcon sx={{ fontSize: 20 }} />
                CRM 회원 찾아서 연결
              </ChoiceTitle>
              <ChoiceDesc>센터에 이미 등록된 회원·잠재회원이면 찾아서 연결합니다.</ChoiceDesc>
            </ChoiceCard>
            <ChoiceCard type="button" active={mode === MODE_NEW} onClick={() => chooseMode(MODE_NEW)}>
              <ChoiceTitle active={mode === MODE_NEW}>
                <PersonAddAlt1Icon sx={{ fontSize: 20 }} />
                잠재회원으로 새로 등록
              </ChoiceTitle>
              <ChoiceDesc>센터에 기록이 없는 처음 온 고객이면 새로 등록합니다.</ChoiceDesc>
            </ChoiceCard>
          </ChoiceRow>
          {mode === MODE_PREVIOUS && previousTarget ? (
            <CompareCard>
              <CompareHeading>예전 연동 데이터 ({previousTypeLabel})</CompareHeading>
              <CompareLine>이름 {previousTarget.name || '-'}</CompareLine>
              <CompareLine>번호 {previousTarget.phoneNumber || '-'}</CompareLine>
            </CompareCard>
          ) : mode === MODE_SEARCH ? (
            <>
              {samePhoneCandidates.length > 0 && (
                <>
                  <CompareHeading>앱에서 인증한 번호와 같은 번호 {samePhoneCandidates.length}명</CompareHeading>
                  <ResultList sx={{ marginBottom: '16px' }}>
                    {samePhoneCandidates.map(item => (
                      <ResultItem key={`same-phone-${item.memberId}`} type="button" onClick={() => setSelected(item)}>
                        <span>
                          {item.name} <ResultSub>{item.phoneNumber}</ResultSub>
                        </span>
                        <ResultSub>
                          {item.userType === TEMPORARY_USER_TYPE ? '잠재회원' : '회원'}
                          {item.birthDate ? ` · ${item.birthDate}` : ''}
                        </ResultSub>
                      </ResultItem>
                    ))}
                  </ResultList>
                  <CompareHeading>목록에 없으면 검색</CompareHeading>
                </>
              )}
              <SearchRow>
                <SearchInput
                  value={keyword}
                  placeholder="이름 또는 전화번호"
                  onChange={e => setKeyword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                />
                <CommonModalButton onClick={handleSearch} style={{ minWidth: '80px', height: '40px' }}>
                  검색
                </CommonModalButton>
              </SearchRow>
              {searched && !isFetching && results.length === 0 && (
                <EmptyText>
                  검색 결과가 없습니다.
                  <br />
                  처음 온 고객이면 [잠재회원으로 새로 등록]을 골라 주세요.
                </EmptyText>
              )}
              <ResultList>
                {results.map(item => (
                  <ResultItem key={`${item.userType}-${item.memberId}`} type="button" onClick={() => setSelected(item)}>
                    <span>
                      {item.name} <ResultSub>{item.phoneNumber}</ResultSub>
                    </span>
                    <ResultSub>{item.userType === TEMPORARY_USER_TYPE ? '잠재회원' : '회원'}</ResultSub>
                  </ResultItem>
                ))}
              </ResultList>
            </>
          ) : (
            <CompareCard>
              <CompareHeading>이 정보로 잠재회원을 등록합니다</CompareHeading>
              <CompareLine>이름 {link.appAccount?.name || '-'}</CompareLine>
              <CompareLine>아이디 {link.appAccount?.username || '-'}</CompareLine>
              <CompareLine>번호 {link.appAccount?.phoneNumber || '-'}</CompareLine>
            </CompareCard>
          )}
        </>
      )}
    </CommonModalLayout>
  );
};

export default ApproveLinkModal;
